import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { submitAnswer } from "@/services/apiService";

const schema = z.object({
  email: z.string().email("Please enter a valid email address"),
  answer: z.string().min(1, "Please share your thoughts"),
});

type FormData = z.infer<typeof schema>;

interface AnswerFormProps {
  questionId: string;
}

export const AnswerForm = ({ questionId }: AnswerFormProps) => {
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    setSubmitError(null);
    try {
      await submitAnswer({ ...data, question_id: questionId });
      setSubmitted(true);
    } catch {
      setSubmitError("Something went wrong. Please try again.");
    }
  };

  if (submitted) {
    return (
      <div className="text-center text-white space-y-2">
        <p className="text-xl font-light">Thank you.</p>
        <p className="text-white/70">We'll be in touch.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 max-w-lg mx-auto w-full">
      <div>
        <Textarea
          {...register("answer")}
          placeholder="Share your thoughts..."
          className="bg-white/10 border-white/20 text-white placeholder:text-white/50 min-h-[120px] resize-none"
        />
        {errors.answer && (
          <p className="text-red-300 text-sm mt-1">{errors.answer.message}</p>
        )}
      </div>
      <div>
        <Input
          {...register("email")}
          type="email"
          placeholder="Your email address"
          className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
        />
        {errors.email && (
          <p className="text-red-300 text-sm mt-1">{errors.email.message}</p>
        )}
      </div>
      <p className="text-white/40 text-xs text-center">
        Your response may inform this system's learning. We won't share your email.
      </p>
      {submitError && <p className="text-red-300 text-sm text-center">{submitError}</p>}
      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-white text-gray-900 hover:bg-white/90 font-medium"
      >
        {isSubmitting ? "Sending..." : "Send"}
      </Button>
    </form>
  );
};
